// Synth Lab v2: mastery tracking + the practice-session scheduler.
//
// Mastery is tracked per (concept, rung) as a ROLLING window of the most
// recent attempts — not lifetime totals — so the stars can move again after
// you improve (or rust). Stars gate the unlock chain: rungs unlock in ladder
// order within a concept, and the next concept opens once the current one's
// "name" rung reaches a star.
//
// The scheduler turns that record into a short session: weakness-weighted
// (fewer stars → more reps), seeded (a daily seed gives the same session all
// day), and capped per concept so a session never tunnels on one knob.
//
// Pure data + functions; the page owns localStorage and timestamps.

import { CONCEPT_ORDER, conceptRungIds, seededRng, wreckablePresets } from "./synthLadder";
import { clamp } from "./synthLab";

/* ============================================================
   The mastery record
   ============================================================ */

export const WINDOW = 12; // rolling attempts kept per rung

export const emptyMastery = () => ({ version: 2, rungs: {} });

export const keyOf = (concept, rung) => `${concept}:${rung}`;

// Fold one result into the record (immutable). `hear` rungs are pass-only:
// completing the lesson records a single correct attempt.
export const recordResult = (mastery, concept, rung, correct) => {
  const k = keyOf(concept, rung);
  const prev = mastery.rungs[k] || { recent: [], attempts: 0, correct: 0 };
  const recent = [...prev.recent, correct ? 1 : 0].slice(-WINDOW);
  return {
    ...mastery,
    rungs: {
      ...mastery.rungs,
      [k]: {
        recent,
        attempts: prev.attempts + 1,
        correct: prev.correct + (correct ? 1 : 0),
      },
    },
  };
};

export const rungStats = (mastery, concept, rung) => {
  const s = mastery.rungs[keyOf(concept, rung)] || { recent: [], attempts: 0, correct: 0 };
  const acc = s.recent.length ? s.recent.reduce((a, b) => a + b, 0) / s.recent.length : null;
  return { ...s, recentAccuracy: acc };
};

// 0–3 stars from the rolling window. Requires enough recent evidence for the
// higher tiers so three lucky guesses can't gild a rung. `hear` is done/not.
export const rungStars = (mastery, concept, rung) => {
  const { recent, recentAccuracy } = rungStats(mastery, concept, rung);
  if (rung === "hear") return recent.length ? 3 : 0;
  if (recent.length < 4 || recentAccuracy == null) return 0;
  if (recentAccuracy >= 0.9 && recent.length >= 8) return 3;
  if (recentAccuracy >= 0.75 && recent.length >= 6) return 2;
  if (recentAccuracy >= 0.55) return 1;
  return 0;
};

/* ============================================================
   Unlock chain
   ============================================================ */

export const isConceptUnlocked = (mastery, concept) => {
  const i = CONCEPT_ORDER.indexOf(concept);
  if (i <= 0) return i === 0;
  return rungStars(mastery, CONCEPT_ORDER[i - 1], "name") >= 1;
};

export const isRungUnlocked = (mastery, concept, rung) => {
  if (!isConceptUnlocked(mastery, concept)) return false;
  const ladder = conceptRungIds(concept);
  const i = ladder.indexOf(rung);
  if (i < 0) return false;
  if (i === 0) return true;
  return rungStars(mastery, concept, ladder[i - 1]) >= 1;
};

export const unlockedConcepts = (mastery) =>
  CONCEPT_ORDER.filter((c) => isConceptUnlocked(mastery, c));

// Every unlocked rung that isn't fully gilded yet — the practice frontier.
export const frontier = (mastery) => {
  const out = [];
  CONCEPT_ORDER.forEach((concept) => {
    conceptRungIds(concept).forEach((rung) => {
      if (!isRungUnlocked(mastery, concept, rung)) return;
      const stars = rungStars(mastery, concept, rung);
      if (stars < 3) out.push({ concept, rung, stars });
    });
  });
  return out;
};

/* ============================================================
   Summary numbers for the journey map
   ============================================================ */

export const totalReps = (mastery) =>
  Object.values(mastery.rungs).reduce((s, r) => s + r.attempts, 0);

export const gildedCount = (mastery) => {
  let n = 0;
  CONCEPT_ORDER.forEach((concept) => {
    conceptRungIds(concept).forEach((rung) => {
      if (rungStars(mastery, concept, rung) >= 3) n++;
    });
  });
  return n;
};

export const totalRungCount = () =>
  CONCEPT_ORDER.reduce((s, c) => s + conceptRungIds(c).length, 0);

/* ============================================================
   Session scheduler
   ============================================================ */

export const SESSION_LENGTH = 8;

// Difficulty level for generated rounds tracks the stars already earned.
const levelFor = (stars) => clamp(stars + 1, 1, 3);

// Weighted sample without replacement-per-draw (items may repeat across the
// session, but never back-to-back for the same rung).
const weightedPick = (items, weights, rng) => {
  const total = weights.reduce((a, b) => a + b, 0);
  let roll = rng() * total;
  for (let i = 0; i < items.length; i++) {
    roll -= weights[i];
    if (roll <= 0) return items[i];
  }
  return items[items.length - 1];
};

// Build a session: an array of {concept, rung, level, reveal?} descriptors.
//
//  - `focus`: practice a single concept's ladder (from the journey map);
//    otherwise mix across the whole frontier.
//  - Un-heard lessons come first, once each — reading is a doorway, not a rep.
//  - When at least two concepts have their `fix` rung unlocked, the session
//    ends in an UNREVEALED fix round: the "what's wrong with this patch?"
//    capstone where the player must diagnose before dialing.
export const buildSession = (mastery, { seed = "practice", length = SESSION_LENGTH, focus = null } = {}) => {
  const rng = seededRng(seed);
  let pool = frontier(mastery);
  if (focus) pool = pool.filter((f) => f.concept === focus);
  if (!pool.length) {
    // Everything gilded (or a gilded focus): maintenance mode over all
    // unlocked rungs at full difficulty.
    CONCEPT_ORDER.filter((c) => !focus || c === focus).forEach((concept) => {
      conceptRungIds(concept).forEach((rung) => {
        if (rung !== "hear" && isRungUnlocked(mastery, concept, rung)) {
          pool.push({ concept, rung, stars: 3 });
        }
      });
    });
  }
  if (!pool.length) return [];

  const session = [];

  // Lessons first (once each), then reps from the remaining pool.
  const lessons = pool.filter((f) => f.rung === "hear");
  lessons.slice(0, 2).forEach((f) => session.push({ concept: f.concept, rung: "hear", level: 1 }));
  const reps = pool.filter((f) => f.rung !== "hear");

  // A lesson unlocks its concept's "name" rung the moment it's completed, so
  // let this session train it right away instead of ending after the reading.
  lessons.slice(0, 2).forEach((f) => {
    if (!reps.some((r) => r.concept === f.concept && r.rung === "name")) {
      reps.push({ concept: f.concept, rung: "name", stars: 0 });
    }
  });

  if (reps.length) {
    let last = null;
    while (session.length < length) {
      const prev = last;
      const candidates = reps.filter((f) => keyOf(f.concept, f.rung) !== prev || reps.length === 1);
      const weights = candidates.map((f) => 4 - f.stars); // 0★ → 4, 2★ → 2
      const chosen = weightedPick(candidates, weights, rng);
      last = keyOf(chosen.concept, chosen.rung);
      session.push({ concept: chosen.concept, rung: chosen.rung, level: levelFor(chosen.stars) });
    }
  }

  // Capstone: an unrevealed fix round across everything fix-ready, but only
  // for concepts where a real preset can be audibly wrecked.
  if (!focus) {
    const fixReady = CONCEPT_ORDER.filter(
      (c) => isRungUnlocked(mastery, c, "fix") && wreckablePresets(c).length
    );
    if (fixReady.length >= 2 && session.length) {
      const concept = fixReady[Math.floor(rng() * fixReady.length)];
      session[session.length - 1] = { concept, rung: "fix", level: 1, reveal: false };
    }
  }

  return session;
};

/* ============================================================
   Persistence coercion — repair anything localStorage hands us
   ============================================================ */

const VALID_KEYS = new Set(
  CONCEPT_ORDER.flatMap((c) => conceptRungIds(c).map((r) => keyOf(c, r)))
);

export const coerceMastery = (raw) => {
  if (!raw || typeof raw !== "object" || !raw.rungs || typeof raw.rungs !== "object") {
    return emptyMastery();
  }
  const rungs = {};
  Object.entries(raw.rungs).forEach(([k, v]) => {
    if (!VALID_KEYS.has(k) || !v || typeof v !== "object") return;
    const recent = Array.isArray(v.recent)
      ? v.recent.slice(-WINDOW).map((x) => (x ? 1 : 0))
      : [];
    const attempts = Number.isFinite(v.attempts)
      ? Math.max(recent.length, Math.floor(v.attempts))
      : recent.length;
    const correct = Number.isFinite(v.correct)
      ? clamp(Math.floor(v.correct), 0, attempts)
      : recent.reduce((a, b) => a + b, 0);
    if (attempts > 0) rungs[k] = { recent, attempts, correct };
  });
  return { version: 2, rungs };
};
